/**
 * BSF Product Drawer
 * ==================
 * Parallel drawer system for BSF Volume Pricing.
 * Custom element: <bsf-product-drawer>
 * Matches product-drawer.js behavior for consistent scroll handling.
 *
 * VERSION: 2026.01.11
 */

class BSFProductDrawer extends HTMLElement {
  constructor() {
    super();

    this.inner = this.querySelector('.bsf-drawer__inner');
    this.container = this.querySelector('.bsf-drawer__container');
    this.closeBtn = this.querySelector('.bsf-drawer__close');
    this.content = this.querySelector('.bsf-drawer__content');

    this.isOpen = false;
    this.cartEventsBound = false;

    // Store bound handler references for cleanup
    this._handleEscapeKey = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    };

    this.bindEvents();
    this.bindCartEvents();
  }

  bindEvents() {
    // Close button
    this.closeBtn?.addEventListener('click', () => this.close());

    // Click outside to close
    this.addEventListener('click', (e) => {
      if (!e.target.closest('.bsf-drawer__container')) {
        this.close();
      }
    });

    // ESC key to close
    document.addEventListener('keydown', this._handleEscapeKey);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._handleEscapeKey);
  }

  /**
   * Bind cart events to close drawer on ATC success
   * This is done once in the main JS, not in dynamically loaded content
   */
  bindCartEvents() {
    if (this.cartEventsBound) return;
    if (typeof window.MinimogEvents === 'undefined') return;

    // Close drawer when cart updates (matches original behavior)
    const closeForCart = () => {
      if (this.isOpen) {
        this.close();
      }
    };

    // Close on cart update (fires after successful add-to-cart)
    window.MinimogEvents.subscribe('cart-update', closeForCart);

    // Close when cart drawer opens
    window.MinimogEvents.subscribe('open-cart-drawer', closeForCart);

    this.cartEventsBound = true;
  }

  /**
   * Open the drawer with a product
   * @param {string} productHandle - The product handle to load
   */
  open(productHandle) {
    if (this.isOpen) return;

    // Show drawer (display: block)
    this.classList.add('bsf-drawer--active');

    // Trigger animation and scroll lock together in rAF
    // CSS handles everything: overflow:hidden + contain:layout on sticky elements
    // No body positioning needed - this avoids sticky element jump issues
    requestAnimationFrame(() => {
      this.style.setProperty('--bsf-drawer-bg-opacity', '0.5');
      this.inner.style.setProperty('--bsf-drawer-translate', '0');
      document.documentElement.classList.add('prevent-scroll');
    });

    this.isOpen = true;

    // Show skeleton while loading
    this.content.setAttribute('aria-busy', 'true');
    this.showLoading();
    this.loadProduct(productHandle);
  }

  /**
   * Close the drawer
   * CSS handles scroll restoration via overflow:hidden removal
   */
  close() {
    if (!this.classList.contains('bsf-drawer--active')) {
      return;
    }

    // Start close animation
    this.style.setProperty('--bsf-drawer-bg-opacity', '0');
    this.inner.style.setProperty('--bsf-drawer-translate', '100%');

    this.isOpen = false;

    // Clean up after animation completes
    setTimeout(() => {
      this.classList.remove('bsf-drawer--active');
      this._safeRemovePreventScroll();
    }, 350);

    // Clear content immediately
    this.content.innerHTML = '';

    // Extra prevent-scroll removal for safety
    setTimeout(() => {
      this._safeRemovePreventScroll();
    }, 100);
  }

  /**
   * Safely remove prevent-scroll class only if no other drawers are open
   * Prevents breaking scroll lock on cart drawer or menu drawer
   */
  _safeRemovePreventScroll() {
    const cartDrawerOpen = document.querySelector('.m-cart-drawer--active');
    const menuDrawerOpen = document.querySelector('.m-menu-drawer--active');
    const productDrawerOpen = document.querySelector('.product-drawer--active');

    if (!cartDrawerOpen && !menuDrawerOpen && !productDrawerOpen) {
      document.documentElement.classList.remove('prevent-scroll');
    }
  }

  /**
   * Show skeleton placeholder while loading
   * Always uses skeleton placeholders (no pre-loaded image to avoid blink)
   */
  showLoading() {
    // Pure skeleton layout - avoids image blink on content load
    this.content.innerHTML = `
      <div class="product-drawer__content bsf-drawer__skeleton">
        <div class="bsf-skeleton bsf-skeleton--image"></div>
        <div class="product-drawer__content-wrapper">
          <div class="bsf-skeleton bsf-skeleton--title"></div>
          <div class="bsf-skeleton bsf-skeleton--headline"></div>
          <div class="bsf-skeleton bsf-skeleton--option"></div>
          <div class="bsf-skeleton bsf-skeleton--option"></div>
          <div class="bsf-skeleton bsf-skeleton--option"></div>
          <div class="bsf-skeleton bsf-skeleton--button"></div>
        </div>
      </div>
    `;
  }

  /**
   * Load product content via AJAX
   * @param {string} handle - Product handle
   */
  async loadProduct(handle) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    try {
      // No timestamp = browser can cache the response for speed
      const response = await fetch(
        `/products/${handle}?section_id=bsf-product-drawer-content`,
        { signal: controller.signal }
      );
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.querySelector('.bsf-drawer-content');

      if (content) {
        // Replace content directly (no clear step to prevent blink)
        // Create a temporary container, build content, then swap
        const temp = document.createElement('div');
        this.insertHTMLWithScripts(temp, content.innerHTML);
        this.content.innerHTML = temp.innerHTML;
      } else {
        // Fallback: use the whole response
        this.content.innerHTML = html;
      }

      // Initialize BSF Volume Pricing if present
      this.content.setAttribute('aria-busy', 'false');
      this.initializeVolumePricing();

    } catch (error) {
      console.error('BSF Drawer: Error loading product', error);
      this.close();
      window.location.href = `/products/${handle}`;
    }
  }

  /**
   * Insert HTML and execute any scripts
   * @param {HTMLElement} target - Target element
   * @param {string} htmlString - HTML to insert
   */
  insertHTMLWithScripts(target, htmlString) {
    const temp = document.createElement('div');
    temp.innerHTML = htmlString;

    // Insert non-script elements
    Array.from(temp.childNodes).forEach(node => {
      if (node.tagName !== 'SCRIPT') {
        target.appendChild(node.cloneNode(true));
      }
    });

    // Execute scripts
    temp.querySelectorAll('script').forEach(script => {
      const newScript = document.createElement('script');
      if (script.src) {
        newScript.src = script.src;
      } else {
        newScript.textContent = script.textContent;
      }
      target.appendChild(newScript);
    });
  }

  /**
   * Initialize BSF Volume Pricing component if loaded
   */
  initializeVolumePricing() {
    // Dispatch event for any listeners
    this.dispatchEvent(new CustomEvent('bsf-drawer:content-loaded', {
      bubbles: true
    }));

    // If BSFVolumePricing exists, it will auto-initialize via event delegation
  }
}

// Register custom element
if (!customElements.get('bsf-product-drawer')) {
  customElements.define('bsf-product-drawer', BSFProductDrawer);
}

// Global helper function (matches existing pattern)
window.openBSFProductDrawer = function(productHandle) {
  const drawer = document.getElementById('BSFProductDrawer');
  if (drawer) {
    drawer.open(productHandle);
  }
};